import { useRef, useEffect } from "react";
import { Send } from "lucide-react";
import CelebrationButton from "../ui/CelebrationButton/CelebrationButton";
import { useSelector } from "react-redux";

export default function PageHeader({
  icon,
  title,
  badge,
  subtitle,
  actions = [],
  onSubmit,
  submitLabel = 'Submit Booking',
  submitIcon = <Send />,
  submitClassName = 'inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 bg-green-500 text-white hover:bg-green-700',
}) {
  const headerRef = useRef(null);
  const { isAnimate } = useSelector((state) => state.toast);
  const { loader } = useSelector((state) => state.loader);

  useEffect(() => {
    const update = () => {
      const h = headerRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty(
        "--page-header-height",
        `${h}px`
      );
    };

    update();

    const ro = new ResizeObserver(update);

    if (headerRef.current) ro.observe(headerRef.current);

    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    // Small timeout ensures DOM is ready before scrolling
    const timer = setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        const main = document.getElementById('main-content');
        if (main) main.scrollTop = 0;
    }, 0);
    return () => clearTimeout(timer);
}, []);

  const variantMap = {
    ghost:
      "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700",

    tertiary:
      "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600",

    black:
      "bg-slate-900 text-white hover:bg-slate-700 dark:bg-slate-600 dark:text-slate-100 dark:hover:bg-slate-500",

    success:
      "bg-green-500 text-white hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-500",
  };

  return (
    <header
      ref={headerRef}
      className="fixed right-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-200 dark:border-slate-700 shadow-sm"
      style={{
        left: "var(--sidebar-width)",
        top: "var(--navbar-height)",
      }}
    >
      <div className="px-3 py-2.5 sm:px-4 sm:py-3 flex items-center justify-between gap-2 sm:gap-4">

        {/* LEFT */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-[9px] sm:rounded-[11px] flex items-center justify-center flex-shrink-0 shadow-md sm:shadow-lg bg-gradient-to-br from-[#2563EB] to-[#14B8A6]">
            <span className="[&>svg]:w-4 [&>svg]:h-4 sm:[&>svg]:w-5 sm:[&>svg]:h-5 text-white">
              {icon}
            </span>
          </div>

          <div className="flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5">
              <h1 className="text-sm sm:text-base lg:text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight leading-none truncate">
                {title}
              </h1>

              {badge && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 whitespace-nowrap">
                  <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
                  {badge}
                </span>
              )}
            </div>

            {subtitle && (
              <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 leading-none truncate hidden xs:block sm:block">
                {subtitle}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT */}
        <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">

          {actions.map((action, i) =>
            action === "|" ? (
              <div
                key={i}
                className="w-px h-5 sm:h-6 bg-slate-200 dark:bg-slate-600 mx-0.5 sm:mx-1"
              />
            ) : (
              <button
                key={i}
                onClick={action.onClick}
                disabled={action.disabled}
                title={action.label}
                className={`
                  inline-flex items-center justify-center gap-1 sm:gap-1.5
                  text-[11px] sm:text-[12.5px] font-medium
                  px-2 sm:px-3 lg:px-4
                  h-8 sm:h-9 lg:h-[34px]
                  rounded-[7px] sm:rounded-[9px]
                  transition-all duration-150
                  disabled:opacity-40 disabled:cursor-not-allowed
                  ${variantMap[action.variant || "tertiary"]}
                `}
              >
                {action.icon && (
                  <span className="w-[14px] h-[14px] sm:w-[15px] sm:h-[15px] flex-shrink-0 [&>svg]:w-full [&>svg]:h-full">
                    {action.icon}
                  </span>
                )}

                {action.label && (
                  <span className="hidden sm:inline whitespace-nowrap">
                    {action.label}
                  </span>
                )}
              </button>
            )
          )}

          {onSubmit && (
            <CelebrationButton
              onClick={onSubmit}
              label={submitLabel}
              isSuccess={isAnimate}
              isLoading={loader}
              className={submitClassName}
              icon={
                <span className="w-[15px] h-[15px] flex-shrink-0 [&>svg]:w-full [&>svg]:h-full">
                  {submitIcon}
                </span>
              }
            />
          )}
        </div>
      </div>
    </header>
  );
}